"use client";

import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { toast } from "@shared/components/ui/snackbar";
import { useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useEffect, useState } from "react";
import { OrgProjectSelect } from "@/components/org/org-project-select";
import { formatUserError, shouldSkipErrorToast } from "@/lib/errors";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";
import { invalidateOrgProjectsAndApiKeys } from "@/lib/org/invalidate-org-queries";
import { uiMetricCount } from "@/lib/sentry/metrics";

const createApiKeyProjectsDocument = gql`
  query CreateApiKeyDialogProjects {
    me {
      org {
        projects {
          id
          name
          color
        }
      }
    }
  }
`;

const createApiKeyDocument = gql`
  mutation CreateApiKeyDialogApiKey($project_id: ID!, $name: String!) {
    createApiKey(project_id: $project_id, name: $name) {
      id
      key_prefix
      purpose
      is_editable
    }
  }
`;

const revealApiKeyDocument = gql`
  mutation CreateApiKeyDialogRevealApiKey($id: ID!) {
    revealApiKey(id: $id) {
      raw_key
    }
  }
`;

type CreateApiKeyProjectsQuery = {
  me: {
    org: {
      projects: Array<{ id: string; name: string; color?: string | null }>;
    } | null;
  };
};

type CreateApiKeyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string | null;
  billingRequired?: boolean;
  onBillingRequired?: () => void;
  onCreated?: (apiKey: { name: string; rawKey: string }) => void | Promise<void>;
};

export const CreateApiKeyDialog = ({
  open,
  onOpenChange,
  projectId,
  billingRequired = false,
  onBillingRequired,
  onCreated,
}: CreateApiKeyDialogProps) => {
  const queryClient = useQueryClient();
  const [keyName, setKeyName] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId ?? null);
  const { data: projectsData } = useGraphqlQuery<CreateApiKeyProjectsQuery>({
    queryKey: ["createApiKeyDialogProjects"],
    document: createApiKeyProjectsDocument,
    enabled: open,
    refetchOnMount: "always",
  });
  const revealMutation = useGraphqlMutation<{ revealApiKey: { raw_key: string } }, { id: string }>({
    document: revealApiKeyDocument,
  });
  const createMutation = useGraphqlMutation<
    { createApiKey: { id: string; key_prefix: string } },
    { project_id: string; name: string }
  >({
    document: createApiKeyDocument,
  });

  const projects = projectsData?.me.org?.projects ?? [];
  const firstProjectId = projects[0]?.id ?? null;

  // Name must reset only when the dialog opens. `firstProjectId` arrives later
  // from the projects query; depending on it here wipes a name the user already typed.
  useEffect(() => {
    if (!open) {
      return;
    }
    setKeyName("");
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedProjectId(projectId ?? firstProjectId);
  }, [open, projectId, firstProjectId]);

  const handleCreate = async () => {
    if (billingRequired) {
      onBillingRequired?.();
      return;
    }

    const name = keyName.trim();
    if (!name) {
      toast.error("Please enter a name for the API key");
      return;
    }

    const resolvedProjectId = projectId ?? selectedProjectId;
    if (!resolvedProjectId) {
      toast.error("No project is available yet. Create a project first.");
      return;
    }

    try {
      const created = await createMutation.mutateAsync({ project_id: resolvedProjectId, name });
      uiMetricCount("ui.api_key.create", 1, { outcome: "success" });
      try {
        const revealed = await revealMutation.mutateAsync({ id: created.createApiKey.id });
        uiMetricCount("ui.api_key.reveal", 1, { outcome: "success" });
        invalidateOrgProjectsAndApiKeys(queryClient);
        setKeyName("");
        onOpenChange(false);
        await onCreated?.({ name, rawKey: revealed.revealApiKey.raw_key });
      } catch (error) {
        uiMetricCount("ui.api_key.reveal", 1, { outcome: "failed" });
        if (!shouldSkipErrorToast(error)) {
          toast.error(formatUserError(error, "Could not create API key"));
        }
      }
    } catch (error) {
      uiMetricCount("ui.api_key.create", 1, { outcome: "failed" });
      if (!shouldSkipErrorToast(error)) {
        toast.error(formatUserError(error, "Could not create API key"));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new API key</DialogTitle>
          <DialogDescription>
            Create an API key to access the Radium API or client SDKs. You can manage your keys in Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="create-api-key-name">API key name</Label>
            <Input
              id="create-api-key-name"
              value={keyName}
              placeholder="odyssey-api-key"
              onChange={(event) => setKeyName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-api-key-project">Project</Label>
            <OrgProjectSelect
              id="create-api-key-project"
              projects={projects}
              value={projectId ?? selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={Boolean(projectId)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => void handleCreate()}
            disabled={
              createMutation.isPending ||
              revealMutation.isPending ||
              !keyName.trim() ||
              !(projectId ?? selectedProjectId)
            }
          >
            Create API key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
